/**
 * History Timeline Components
 * 
 * Reusable timeline components for displaying document activity history.
 * 
 * Architecture:
 * - HistoryTimeline: Pure presentational component (no data fetching)
 * - HistoryTimelineItem: Single item renderer with icon, content, actions
 * - Helper functions: Icons, content rendering, actions (extensible)
 * 
 * Usage:
 * - Container components (DocSidebarHistoryTimeline) handle data fetching
 * - Pass items, loading states, and callbacks as props
 */

import { Loader2, Paperclip, UserPlus, Share2, Heart, MessageCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DocChatItem } from "@/lib/erp/docs";
import type { LucideIcon } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface HistoryTimelineProps {
  items: DocChatItem[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
}

interface HistoryTimelineItemProps {
  item: DocChatItem;
  isLast: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format timestamp for display
 */
function formatTimestamp(creation: string): string {
  try {
    const d = new Date(creation);
    if (Number.isNaN(d.getTime())) return creation;
    return d.toLocaleString();
  } catch {
    return creation;
  }
}

/**
 * Get icon component for activity type
 * 
 * Returns a Lucide icon component based on the item's type and event_type meta.
 * This is the single source of truth for activity icons.
 */
function getActivityIcon(item: DocChatItem): LucideIcon | null {
  const eventType = (item.meta?.event_type || item.type || "").toLowerCase();

  if (eventType.includes("comment")) return MessageCircle;
  if (eventType.includes("attachment") || eventType.includes("file")) return Paperclip;
  if (eventType.includes("assigned") || eventType.includes("assignment")) return UserPlus;
  if (eventType.includes("shared") || eventType.includes("unshared")) return Share2;
  if (eventType.includes("like")) return Heart;
  if (eventType.includes("version") || eventType.includes("changed") || eventType.includes("field")) return FileText;

  return null;
}

/**
 * Render item content (HTML or plain text)
 * 
 * Uses the same content field as Chat & Activity panel (backend-sanitized HTML).
 * Falls back to plain_text if available, otherwise minimal type-based fallback.
 */
function renderItemContent(item: DocChatItem): React.ReactNode {
  // Prefer the same field used in Chat & Activity panel (backend-sanitized HTML)
  if (item.content) {
    return (
      <span
        dangerouslySetInnerHTML={{ __html: item.content }}
      />
    );
  }

  // Fallback to plain text if available
  if (item.plain_text) {
    return <span>{item.plain_text}</span>;
  }

  // Minimal fallback by type (should rarely be needed)
  const eventType = (item.meta?.event_type || item.type || "").toLowerCase();
  if (eventType.includes("assignment")) return <span>Assignment updated.</span>;
  if (eventType.includes("attachment")) return <span>File updated.</span>;
  if (eventType.includes("version")) return <span>Document updated.</span>;

  return <span>Activity</span>;
}

/**
 * Render action buttons for an item
 * 
 * Extensible pattern for adding "a lot more buttons" per item type.
 * Returns null if no actions are available.
 */
function renderItemActions(item: DocChatItem): React.ReactNode {
  const eventType = (item.meta?.event_type || item.type || "").toLowerCase();
  const actions: React.ReactNode[] = [];

  // Comment actions
  if (eventType.includes("comment") || item.type === "comment") {
    // Future: Add "View in chat" button that scrolls to comment in main panel
    // actions.push(
    //   <Button key="view" variant="ghost" size="xs" className="h-6 px-2">
    //     View in chat
    //   </Button>
    // );
  }

  // Attachment actions
  if (eventType.includes("attachment") || eventType.includes("file")) {
    // Future: Add file URL from meta when backend provides it
    // if (item.meta?.file_url) {
    //   actions.push(
    //     <Button
    //       key="open-file"
    //       variant="ghost"
    //       size="xs"
    //       className="h-6 px-2"
    //       asChild
    //     >
    //       <a href={item.meta.file_url} target="_blank" rel="noreferrer">
    //         Open file
    //       </a>
    //     </Button>
    //   );
    // }
  }

  // Version actions
  if (eventType.includes("version")) {
    // Future: Add "View version" button that opens version diff dialog
    // if (typeof item.meta?.version === "number") {
    //   actions.push(
    //     <Button key="view-version" variant="ghost" size="xs" className="h-6 px-2">
    //       View version {item.meta.version}
    //     </Button>
    //   );
    // }
  }

  // Return null if no actions (keeps UI clean)
  if (!actions.length) return null;

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {actions}
    </div>
  );
}

// ============================================================================
// Components
// ============================================================================

/**
 * HistoryTimelineItem
 * 
 * Single timeline item renderer with:
 * - Icon/dot indicator
 * - Content (HTML or text)
 * - Actor and timestamp
 * - Action buttons (if any)
 */
function HistoryTimelineItem({ item, isLast }: HistoryTimelineItemProps) {
  const Icon = getActivityIcon(item);
  const timestamp = formatTimestamp(item.creation);
  const actor = item.user_full_name || item.owner;

  return (
    <div className="flex gap-3 relative">
      {/* Timeline dot / icon */}
      <div className="flex flex-col items-center flex-shrink-0 relative">
        {Icon ? (
          <div className="h-6 w-6 rounded-full bg-muted/40 flex items-center justify-center flex-shrink-0 z-10">
            <Icon className="h-3 w-3 text-muted-foreground/70" />
          </div>
        ) : (
          <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/50 ring-2 ring-background shadow-sm flex-shrink-0 z-10" />
        )}
        {!isLast && (
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-[1.5px] h-full min-h-[calc(100%+0.75rem)] bg-muted-foreground/20" />
        )}
      </div>

      {/* Content */}
      <div className="space-y-1.5 min-w-0 flex-1 pb-1">
        <div className="text-sm sm:text-xs text-foreground leading-relaxed">
          {renderItemContent(item)}
        </div>
        <div className="text-xs sm:text-[10px] text-muted-foreground/70 flex items-center gap-1.5">
          <span className="font-medium">{actor}</span>
          <span>•</span>
          <span>{timestamp}</span>
        </div>

        {/* Actions row – extensible for "a lot more buttons" */}
        {renderItemActions(item)}
      </div>
    </div>
  );
}

/**
 * HistoryTimeline
 * 
 * Pure presentational component for displaying a timeline of document activities.
 * 
 * Responsibilities:
 * - Render timeline items
 * - Show loading states
 * - Handle "Load more" button
 * 
 * Does NOT:
 * - Fetch data (container components handle that)
 * - Know about useDocChat or React Query
 * - Know about doctype/name (just receives items)
 */
export function HistoryTimeline({
  items,
  isLoading,
  isFetchingMore,
  hasMore,
  onLoadMore,
}: HistoryTimelineProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="text-xs text-muted-foreground/70 py-4 text-center">
        No activity yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Timeline items */}
      <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
        {items.map((item, index) => (
          <HistoryTimelineItem
            key={item.name}
            item={item}
            isLast={index === items.length - 1}
          />
        ))}
      </div>

      {/* Load more button */}
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingMore}
            className="h-8 px-3 text-xs rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetchingMore ? (
              <>
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                Loading…
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}














